import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SalesReturnService } from '../../services/salesReturn.service';
import { ReturnCondition, Returnable } from '../../interfaces/sales/salesReturn.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { humanizeEnum } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';

interface Entry { quantity: string; condition: ReturnCondition | ''; }

/** Records a return against a posted delivery challan (/sales/challans/:challanId/return). */
const SalesReturnForm: React.FC = () => {
  const { challanId: challanParam, id: returnId } = useParams<{ challanId?: string; id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const [data, setData] = useState<Returnable | null>(null);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [returnDate, setReturnDate] = useState(toLocalIsoDate());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit mode (/sales/returns/:id/edit): the draft's own lines are prefilled and saving uses PUT.
  useEffect(() => {
    (async () => {
      try {
        if (returnId) {
          const existing = await SalesReturnService.get(returnId);
          if (existing.status !== 'DRAFT') { toast.showApiError(new Error('Only a draft can be edited'), 'Only a draft return can be edited'); return; }
          setReturnDate(existing.returnDate);
          setReason(existing.reason);
          setEntries(Object.fromEntries(existing.lines.map(l => [l.deliveryChallanLineId, { quantity: String(l.quantity), condition: l.condition }])));
          setData(await SalesReturnService.returnable(existing.deliveryChallanId));
        } else {
          setData(await SalesReturnService.returnable(challanParam!));
        }
      } catch (e) { toast.showApiError(e, 'Failed to load the challan lines'); }
    })();
  }, [challanParam, returnId]); // eslint-disable-line react-hooks/exhaustive-deps

  const entry = (id: string): Entry => entries[id] ?? { quantity: '', condition: '' };
  const set = (id: string, patch: Partial<Entry>) => setEntries(prev => ({ ...prev, [id]: { ...entry(id), ...patch } }));

  if (!data) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const chosen = data.lines.filter(l => Number(entry(l.deliveryChallanLineId).quantity) > 0);
  const overLimit = data.lines.some(l => Number(entry(l.deliveryChallanLineId).quantity) > l.available);
  const missingCondition = chosen.some(l => !entry(l.deliveryChallanLineId).condition);
  const valid = chosen.length > 0 && !overLimit && !missingCondition && !!reason.trim() && !!returnDate;

  const save = async () => {
    try {
      setSaving(true);
      const dto = {
        deliveryChallanId: data.deliveryChallanId, returnDate, reason: reason.trim(),
        lines: chosen.map(l => ({
          deliveryChallanLineId: l.deliveryChallanLineId,
          quantity: Number(entry(l.deliveryChallanLineId).quantity),
          condition: entry(l.deliveryChallanLineId).condition as ReturnCondition,
        })),
      };
      const created = returnId ? await SalesReturnService.update(returnId, dto) : await SalesReturnService.create(institutionId!, dto);
      toast.showSuccess('Return saved as a draft');
      navigate(`/sales/returns/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to save the return');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.returnForm.page">
      <Typography variant="h5" sx={{ mb: 2 }}>{returnId ? 'Edit draft return' : 'Record return'} against {data.challanNo}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {data.invoiceIssued
          ? `Invoiced on ${data.invoiceNumber}. After posting you can create a credit note from the return.`
          : 'Not invoiced yet: the return reduces the challan’s delivered quantity and no credit note is needed.'}
      </Alert>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }} data-help-section="sales.returnForm.section.header">
        <Typography sx={{ flex: '1 1 240px' }}>Customer: <b>{data.customerName}</b></Typography>
        <TextField size="small" data-help="sales.returnForm.returnDate" type="date" label="Return date" value={returnDate} InputLabelProps={{ shrink: true }}
          inputProps={{ max: toLocalIsoDate() }} sx={{ flex: '1 1 180px', maxWidth: 240 }} onChange={e => setReturnDate(e.target.value)} />
        <TextField size="small" data-help="sales.returnForm.reason" label="Reason" value={reason} sx={{ flex: '2 1 280px', maxWidth: 560 }} onChange={e => setReason(e.target.value)} />
      </Paper>
      <Paper sx={{ p: 2, mb: 2 }} data-help-section="sales.returnForm.lines">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell data-help="sales.returnForm.col.item">Item</TableCell><TableCell align="right" data-help="sales.returnForm.col.delivered">Delivered</TableCell><TableCell align="right" data-help="sales.returnForm.col.returned">Returned</TableCell>
            <TableCell align="right" data-help="sales.returnForm.col.available">Available</TableCell><TableCell data-help="sales.returnForm.col.returnQty">Return qty</TableCell><TableCell data-help="sales.returnForm.col.condition">Condition</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.lines.map(l => {
              const e = entry(l.deliveryChallanLineId);
              const over = Number(e.quantity) > l.available;
              return (
                <TableRow key={l.deliveryChallanLineId}>
                  <TableCell>{l.itemCode ? `${l.itemCode} - ${l.itemName}` : (l.description ?? 'Service')}</TableCell>
                  <TableCell align="right">{l.delivered}</TableCell>
                  <TableCell align="right">{l.returned}</TableCell>
                  <TableCell align="right">{l.available}</TableCell>
                  <TableCell>
                    <TextField data-help="sales.returnForm.col.returnQty" size="small" type="number" value={e.quantity} disabled={l.available <= 0} error={over}
                      helperText={over ? `At most ${l.available}` : undefined} inputProps={{ min: 0, max: l.available, 'aria-label': `Return quantity ${l.itemCode ?? ''}` }}
                      onChange={ev => set(l.deliveryChallanLineId, { quantity: ev.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField data-help="sales.returnForm.col.condition" select size="small" value={e.condition} disabled={l.available <= 0} sx={{ minWidth: 140 }}
                      error={Number(e.quantity) > 0 && !e.condition} helperText={Number(e.quantity) > 0 && !e.condition ? 'Pick a condition' : undefined}
                      SelectProps={{ displayEmpty: true, renderValue: v => (v ? humanizeEnum(String(v)) : 'Select...') }}
                      inputProps={{ 'aria-label': `Condition ${l.itemCode ?? ''}` }}
                      onChange={ev => set(l.deliveryChallanLineId, { condition: ev.target.value as ReturnCondition })}>
                      <MenuItem value="RESALEABLE">Resaleable</MenuItem>
                      <MenuItem value="DAMAGED">Damaged</MenuItem>
                    </TextField>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button data-help="sales.returnForm.back" onClick={() => navigate(returnId ? `/sales/returns/${returnId}` : `/sales/challans/${data.deliveryChallanId}`)}>Back</Button>
        <Button variant="contained" data-help="sales.returnForm.save" disabled={!valid || saving} onClick={save}>Save draft</Button>
      </Box>
    </Box>
  );
};

export default SalesReturnForm;
